/**
 * Google / Apple sign-in for the auth gateway.
 *
 * The web session is a bearer token, never a cookie, so the usual redirect flow can't hand
 * it back to the app. Better Auth's oauth-popup runs the provider flow in a popup that is
 * a top-level page on the auth origin, then posts the session token to this window.
 */

import { getSession, socialPopupStartUrl } from './authApi'
import { establishSession } from './authSession'
import type { SocialProvider } from './authApi'
import { AUTH_URL } from '@/lib/config'
import { logger } from '@/lib/monitoring/logger'
import { LogCategory } from '@/lib/monitoring/types'

/** The `type` oauth-popup's completion page posts. */
export const POPUP_MESSAGE_TYPE = 'better-auth:oauth-popup'
const POPUP_TIMEOUT_MS = 5 * 60_000
const POPUP_CLOSED_POLL_MS = 500
const POPUP_WIDTH = 500
const POPUP_HEIGHT = 640

/**
 * `popup_blocked` / `popup_closed` / `timeout` come from here; anything else is the
 * server's error code (e.g. `account_not_linked`).
 */
export class SocialSignInError extends Error {
  readonly code: string

  constructor(code: string, message: string) {
    super(message)
    this.name = 'SocialSignInError'
    this.code = code
  }
}

interface PopupMessage {
  type?: unknown
  nonce?: unknown
  token?: unknown
  error?: { code?: unknown; description?: unknown }
}

/** Sign in with Google or Apple. Call straight from a click handler, or the popup is blocked. */
export async function signInWithSocial(provider: SocialProvider): Promise<void> {
  const nonce = crypto.randomUUID()
  // Opened before any await, while the click still counts as a user gesture.
  const popup = window.open(socialPopupStartUrl(provider, window.location.origin, nonce), 'moly-social-sign-in', popupFeatures())
  if (!popup) throw new SocialSignInError('popup_blocked', 'The sign-in popup was blocked')

  const sessionToken = await waitForPopupToken(popup, nonce, new URL(AUTH_URL).origin)
  const { data: user } = await getSession(sessionToken)
  const outcome = await establishSession({ sessionToken, user })
  logger.logEvent(LogCategory.AUTH_LOGIN, 'signin.social.success', { provider, tokenOutcome: outcome })
}

/**
 * Resolves with the session token the popup posts. Only a message from the auth origin
 * carrying this attempt's nonce counts, so another window can't inject a session.
 */
export function waitForPopupToken(popup: Window, nonce: string, authOrigin: string): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const finish = (settle: () => void) => {
      window.removeEventListener('message', onMessage)
      window.clearInterval(closedPoll)
      window.clearTimeout(timeout)
      settle()
    }

    const onMessage = (event: MessageEvent<PopupMessage | null>) => {
      if (event.origin !== authOrigin) return
      const data = event.data
      if (data?.type !== POPUP_MESSAGE_TYPE || data.nonce !== nonce) return
      const token = data.token
      if (typeof token === 'string' && token) {
        finish(() => resolve(token))
        return
      }
      const code = typeof data.error?.code === 'string' ? data.error.code : 'sign_in_failed'
      const description = typeof data.error?.description === 'string' ? data.error.description : code
      finish(() => reject(new SocialSignInError(code, description)))
    }

    // The completion page closes the popup after posting, so a closed popup with no
    // message means the user closed it. The message is handled first when both happen.
    const closedPoll = window.setInterval(() => {
      if (popup.closed) {
        finish(() => reject(new SocialSignInError('popup_closed', 'The sign-in popup was closed')))
      }
    }, POPUP_CLOSED_POLL_MS)

    const timeout = window.setTimeout(() => {
      popup.close()
      finish(() => reject(new SocialSignInError('timeout', 'Sign-in took too long')))
    }, POPUP_TIMEOUT_MS)

    window.addEventListener('message', onMessage)
  })
}

/** Centred over the current window. */
function popupFeatures(): string {
  const left = Math.round(window.screenX + (window.outerWidth - POPUP_WIDTH) / 2)
  const top = Math.round(window.screenY + (window.outerHeight - POPUP_HEIGHT) / 2)
  return `popup=yes,width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top}`
}
